import {
  OkResponse,
  RecoveryCodes,
  TwoFactorSetup,
  TwoFactorStatus,
  type TwoFactorSetup as Setup,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Icon } from '../../components/Icon';
import { Drawer, Modal } from '../../components/Overlays';
import { PasswordInput } from '../../components/PasswordInput';
import { Alert, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { ApiClientError, api } from '../../lib/api';
import { copyText, errorMessage, formatWhen } from '../../lib/messages';
import { useMe } from '../../lib/session';
import { QrImage } from '../inventory/qr';

export const twoFactorKey = ['two-factor'] as const;

/** Which field the API refused (`details.field`), for the inline message. */
function refusedField(err: unknown): 'password' | 'code' | null {
  if (!(err instanceof ApiClientError) || err.code !== 'bad_request') return null;
  const field: unknown =
    typeof err.details === 'object' && err.details ? Reflect.get(err.details, 'field') : null;
  return field === 'password' || field === 'code' ? field : null;
}

/** "JBSW Y3DP EHPK 3PXP": the key in groups of four, easier to type into the app. */
const grouped = (key: string) => key.replace(/(.{4})/g, '$1 ').trim();

function RecoveryCodesView({ codes, email }: { codes: string[]; email: string }) {
  const toast = useToast();
  const text = [
    'Deizi3d · códigos de recuperação',
    email,
    'Cada código entra uma vez, no lugar do código do app.',
    '',
    ...codes,
  ].join('\n');
  const download = () => {
    const url = URL.createObjectURL(new Blob([`${text}\n`], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'deizi3d-codigos-de-recuperacao.txt';
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="col" style={{ gap: 12 }}>
      <Alert tone="warn">
        <b>Guarde estes códigos agora.</b> Eles aparecem só desta vez. Se você perder o celular,
        cada código entra uma vez no lugar do código do app. Guarde no gerenciador de senhas ou
        imprima.
      </Alert>
      <ul
        aria-label="Códigos de recuperação"
        className="card sunk tab"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 14,
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
          fontSize: 16,
          textAlign: 'center',
        }}
      >
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <div className="row wrap" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn"
          onClick={() =>
            void copyText(text).then((ok) =>
              toast(ok ? 'Códigos copiados' : 'Não foi possível copiar'),
            )
          }
        >
          <Icon name="copy" size={16} />
          Copiar
        </button>
        <button type="button" className="btn" onClick={download}>
          Baixar .txt
        </button>
      </div>
    </div>
  );
}

/** Ativar: password → QR code and first code → recovery codes. */
function SetupDrawer({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<Setup | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);

  const start = useMutation({
    mutationFn: () => api.post('/me/two-factor/setup', TwoFactorSetup, { password }),
    onSuccess: setSetup,
  });
  const enable = useMutation({
    mutationFn: () => api.post('/me/two-factor/enable', RecoveryCodes, { code }),
    onSuccess: (r) => {
      setCodes(r.recoveryCodes);
      toast('Verificação em duas etapas ativada');
      void qc.invalidateQueries({ queryKey: twoFactorKey });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => setCode(''),
  });

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    if (password) start.mutate();
  };
  const submitCode = (e: FormEvent) => {
    e.preventDefault();
    if (code.length === 6) enable.mutate();
  };

  return (
    <Drawer kicker="Sua conta" title="Ativar verificação em duas etapas" onClose={onClose}>
      {codes ? (
        <div className="col" style={{ gap: 16 }}>
          <RecoveryCodesView codes={codes} email={me.email} />
          <button type="button" className="btn p block" onClick={onClose}>
            Pronto, guardei
          </button>
        </div>
      ) : setup ? (
        <form className="col" style={{ gap: 16 }} onSubmit={submitCode} noValidate>
          <ol className="sm col" style={{ gap: 6, paddingLeft: 18, margin: 0 }}>
            <li>
              Instale um app autenticador no celular: Google Authenticator, Microsoft Authenticator,
              1Password ou o app de senhas do iPhone.
            </li>
            <li>No app, escolha adicionar conta e leia o QR code.</li>
            <li>Digite abaixo o código de 6 dígitos que aparecer.</li>
          </ol>
          <div className="card sunk col" style={{ alignItems: 'center', gap: 10, padding: 16 }}>
            <QrImage text={setup.otpauthUri} size={184} alt="QR code para o app autenticador" />
            <p className="xs muted">Sem câmera? Digite esta chave no app:</p>
            <code className="tab" style={{ fontSize: 15, wordBreak: 'break-all' }}>
              {grouped(setup.secret)}
            </code>
          </div>
          <div className="field">
            <label htmlFor="tf-code">Código do app</label>
            <input
              id="tf-code"
              className={refusedField(enable.error) === 'code' ? 'input err tab' : 'input tab'}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{ fontSize: 20, letterSpacing: 6, textAlign: 'center' }}
            />
            {refusedField(enable.error) === 'code' ? (
              <span className="help err">
                <Icon name="alert" size={14} />
                Código incorreto. Use o código que aparece agora e confira a hora do celular.
              </span>
            ) : null}
          </div>
          {enable.isError && !refusedField(enable.error) ? (
            <Alert tone="err">{errorMessage(enable.error)}</Alert>
          ) : null}
          <button
            type="submit"
            className="btn p block"
            disabled={code.length !== 6 || enable.isPending}
          >
            Ativar
          </button>
          <p className="xs muted">
            Ao ativar, as suas outras sessões abertas são encerradas: entre de novo nelas com o
            código.
          </p>
        </form>
      ) : (
        <form className="col" style={{ gap: 16 }} onSubmit={submitPassword} noValidate>
          <p className="sm">
            Além da senha, a entrada vai pedir um código do app autenticador no seu celular. Quem
            descobrir a sua senha não entra sem o celular.
          </p>
          <div className="field">
            <label htmlFor="tf-pw">Sua senha</label>
            <PasswordInput
              id="tf-pw"
              value={password}
              onChange={setPassword}
              invalid={refusedField(start.error) === 'password'}
              autoComplete="current-password"
            />
            {refusedField(start.error) === 'password' ? (
              <span className="help err">
                <Icon name="alert" size={14} />
                Senha incorreta.
              </span>
            ) : null}
          </div>
          {start.isError && !refusedField(start.error) ? (
            <Alert tone="err">{errorMessage(start.error)}</Alert>
          ) : null}
          <button type="submit" className="btn p block" disabled={!password || start.isPending}>
            Continuar
          </button>
        </form>
      )}
    </Drawer>
  );
}

/** Desativar, or new recovery codes: both need the password and a current code. */
function ConfirmModal({ kind, onClose }: { kind: 'disable' | 'codes'; onClose: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const action = useMutation({
    mutationFn: async () => {
      const body = { password, code: code.trim() };
      if (kind === 'codes') {
        return (await api.post('/me/two-factor/recovery-codes', RecoveryCodes, body)).recoveryCodes;
      }
      await api.post('/me/two-factor/disable', OkResponse, body);
      return null;
    },
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: twoFactorKey });
      void qc.invalidateQueries({ queryKey: ['users'] });
      if (result) {
        setCodes(result);
        return;
      }
      toast('Verificação em duas etapas desativada');
      onClose();
    },
  });
  const field = refusedField(action.error);
  const title =
    kind === 'disable' ? 'Desativar verificação em duas etapas?' : 'Gerar novos códigos?';

  if (codes) {
    return (
      <Modal
        title="Novos códigos de recuperação"
        onClose={onClose}
        footer={
          <button type="button" className="btn p" onClick={onClose}>
            Pronto, guardei
          </button>
        }
      >
        <RecoveryCodesView codes={codes} email={me.email} />
      </Modal>
    );
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className={kind === 'disable' ? 'btn d' : 'btn p'}
            disabled={!password || code.trim().length < 6 || action.isPending}
            onClick={() => action.mutate()}
          >
            {kind === 'disable' ? 'Desativar' : 'Gerar códigos'}
          </button>
        </>
      }
    >
      <p className="sm">
        {kind === 'disable'
          ? 'A entrada volta a pedir só a senha. Confirme com a senha e um código do app (ou de recuperação).'
          : 'Os códigos de recuperação atuais param de funcionar. Confirme com a senha e um código do app.'}
      </p>
      <div className="field">
        <label htmlFor="tfc-pw">Sua senha</label>
        <PasswordInput
          id="tfc-pw"
          value={password}
          onChange={setPassword}
          invalid={field === 'password'}
          autoComplete="current-password"
        />
        {field === 'password' ? (
          <span className="help err">
            <Icon name="alert" size={14} />
            Senha incorreta.
          </span>
        ) : null}
      </div>
      <div className="field">
        <label htmlFor="tfc-code">Código do app ou de recuperação</label>
        <input
          id="tfc-code"
          className={field === 'code' ? 'input err tab' : 'input tab'}
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        {field === 'code' ? (
          <span className="help err">
            <Icon name="alert" size={14} />
            Código incorreto.
          </span>
        ) : null}
      </div>
      {action.isError && !field ? <Alert tone="err">{errorMessage(action.error)}</Alert> : null}
    </Modal>
  );
}

/** "Sua conta · Verificação em duas etapas" on Configurações · Usuários (no board). */
export function TwoFactorSection() {
  const status = useQuery({
    queryKey: twoFactorKey,
    queryFn: () => api.get('/me/two-factor', TwoFactorStatus),
  });
  const [open, setOpen] = useState<'setup' | 'disable' | 'codes' | null>(null);
  const s = status.data;

  return (
    <section className="card flat" aria-busy={status.isPending}>
      <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 8 }}>
        <div className="col" style={{ gap: 2 }}>
          <span className="xs muted">Sua conta</span>
          <h2>Verificação em duas etapas</h2>
        </div>
        {s?.enabled ? (
          <span className="badge ok">
            <Icon name="check" size={14} />
            Ativada
          </span>
        ) : s ? (
          <span className="badge">Desativada</span>
        ) : null}
      </div>
      <div className="col" style={{ padding: '6px 18px 18px', gap: 12 }}>
        {status.isPending ? <Skeleton /> : null}
        {status.isError ? (
          <ErrorBox
            title="Não foi possível carregar a verificação"
            onRetry={() => void status.refetch()}
          />
        ) : null}
        {s && !s.available && !s.enabled ? (
          <p className="sm muted">
            Ainda não disponível neste servidor: falta configurar a chave <code>AUTH_SECRET</code>{' '}
            (docs/DEPLOY.md, “Proteção do login”).
          </p>
        ) : null}
        {s?.available && !s.enabled ? (
          <>
            <p className="sm">
              Peça um código do app autenticador do celular a cada entrada, além da senha. Se a
              senha vazar, ninguém entra sem o seu celular.
            </p>
            <div>
              <button type="button" className="btn p" onClick={() => setOpen('setup')}>
                Ativar
              </button>
            </div>
          </>
        ) : null}
        {s?.enabled ? (
          <>
            <p className="sm">
              Ativada {formatWhen(s.enabledAt)}. A entrada pede o código do app autenticador.
            </p>
            <div>
              <span className={s.recoveryCodesLeft <= 3 ? 'badge warn' : 'badge'}>
                {s.recoveryCodesLeft}{' '}
                {s.recoveryCodesLeft === 1
                  ? 'código de recuperação restante'
                  : 'códigos de recuperação restantes'}
              </span>
            </div>
            <div className="row wrap" style={{ gap: 8 }}>
              <button type="button" className="btn" onClick={() => setOpen('codes')}>
                Gerar novos códigos
              </button>
              <button type="button" className="btn g" onClick={() => setOpen('disable')}>
                Desativar
              </button>
            </div>
          </>
        ) : null}
      </div>
      {open === 'setup' ? <SetupDrawer onClose={() => setOpen(null)} /> : null}
      {open === 'disable' || open === 'codes' ? (
        <ConfirmModal kind={open} onClose={() => setOpen(null)} />
      ) : null}
    </section>
  );
}
